import { useAuth } from "../../context/AuthContext";
import { formatTimestamp } from "../../lib/time";
import {
  Braces,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  FileUp,
  HelpCircle,
  Maximize2,
  MinusCircle,
  Plus,
  RefreshCw,
  Star,
  TextQuote,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import * as XLSX from "xlsx";
import { cn } from "../../lib/utils";
import { ToggleSwitch } from "../../components/ui/toggle-switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { PromptMarkdown } from "./prompt-markdown";
import { OutcomeSectionsEditor } from "./outcome-sections-editor";
import { InlineFindings } from "./outcome-findings";
import { EMPTY_SECTIONS, compileSections, switchToFourBoxes, usesFourBoxes, type OutcomeSections } from "./outcome-sections";
import type { OutcomeFinding } from "./outcome-validation";
import {
  DEFAULT_SWITCH_PERCENT,
  EMPTY_LANGUAGE_PREFERENCE,
  readReplyLanguage,
  REPLY_LANGUAGE_LABELS,
  replyLanguagesForStt,
  type LanguagePreference,
  type ReplyLanguage,
} from "../../lib/languagePreference";

export interface StudioValidValue {
  value: string;
  description?: string;
}

export interface StudioCallField {
  fieldName: string;
  fieldType?: string;
  description?: string;
  visible?: boolean;
  required?: boolean;
  normalise?: boolean;
  emphasis?: boolean;
  to_lowercase?: boolean;
  number_to_words?: boolean;
  infield_language?: string;
  validValues?: StudioValidValue[];
  // Outcomes only: the four boxes of a new outcome. When set, core builds `description` from them.
  sections?: Partial<OutcomeSections> | null;
  // Outcomes only: intentionally free text, no fixed list of values.
  freeText?: boolean;
  // Outcomes only: issues accepted with a reason on an earlier publish; core keeps them quiet.
  acceptedFindings?: Array<{ rule_id: string; reason: string }>;
  // Outcomes only: what the post-call worker writes for this field when a call
  // is finalized without LLM analysis (short-call retry / LLM skip).
  defaultValue?: string;
  // Outcomes only: auto-reschedule the contact when the post-call LLM's
  // extracted value for this field case-insensitively matches one of these.
  // Empty/absent = disabled for this field. Several outcomes can each carry
  // their own list — matching ANY one of them is enough to trigger a retry.
  // Lowest priority: skipped whenever the call already has an explicit
  // user-requested callback (e.g. "call me back in 5 minutes").
  retryValues?: string[];
  // Outcomes only: single-winner group. When the agent's "Only one outcome
  // per call" toggle is on, outcomes sharing a group compete with every other
  // group and only the highest one in list order is kept (rest forced to NA).
  // Empty = independent, never cleared.
  exclusive_group?: string;
  // Outcomes only: priority of this field's group (1 = highest). Shown only
  // when a group is set. A pair uses its smallest number; empty / ties fall
  // back to list order.
  outcome_priority?: number | null;
  // Server-stamped copy of the agent toggle (for the post-call worker). Read
  // only here; the toggle itself lives on the version.
  outcome_single_winner?: boolean;
  // Outcomes only: marks this as the primary/headline outcome for the agent.
  // At most one outcome per agent may carry this flag. Surfaced as a KPI on
  // the Home dashboard.
  is_primary?: boolean;
  // Outcomes only: explicit list of sub-dispositions / values that count as
  // a primary KPI hit. Empty = any non-negative value counts.
  successValues?: string[];
}

export interface StudioSentence {
  name: string;
  sentence: string;
}

export interface StudioTransferConfig {
  enabled: boolean;
  destinations: Record<string, string>;
  on_time_limit_enabled: boolean;
  on_time_limit_label: string;
  on_time_limit_message: string;
  after_questions_enabled: boolean;
  after_questions_count: number;
  after_questions_label: string;
  after_questions_message: string;
}

export interface StudioFunctionTool {
  function_tool_id?: string;
  name?: string;
  actual_name?: string;
  keyword?: string;
  search_keyword?: string;
  tool_keyword?: string;
}

export interface StudioVersion {
  version_id: string;
  version_name?: string;
  system_prompt?: string;
  first_message?: string;
  call_infields?: StudioCallField[];
  call_outcomes?: StudioCallField[];
  tool?: StudioFunctionTool[];
  tools?: StudioFunctionTool[];
  status?: string;
  published_at?: string;
  created_by?: string;
  is_summary?: boolean;
}

export interface PromptTokenOption {
  key?: string;
  label?: string;
  kind?: string;
  description?: string;
  insertText?: string;
}

const FIELD_TYPES = ["string", "number", "boolean", "date", "email", "phone", "multi-select"];
const DEFAULT_TRANSFER: StudioTransferConfig = {
  enabled: false,
  destinations: {},
  on_time_limit_enabled: false,
  on_time_limit_label: "",
  on_time_limit_message: "",
  after_questions_enabled: false,
  after_questions_count: 3,
  after_questions_label: "",
  after_questions_message: "",
};

function moveItem<T>(items: T[], from: number, to: number) {
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

function IconButton({
  label,
  children,
  disabled,
  danger,
  onClick,
}: {
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-ink-3 transition-colors",
        "hover:border-line hover:bg-elevated hover:text-ink disabled:cursor-not-allowed disabled:opacity-25",
        danger && "hover:border-danger/20 hover:bg-danger-soft hover:text-danger",
      )}
    >
      {children}
    </button>
  );
}

export function PromptTokenMenu({
  options,
  selectedIndex,
  onSelect,
  onHover,
  style,
}: {
  options: PromptTokenOption[];
  selectedIndex: number;
  onSelect: (option: PromptTokenOption) => void;
  onHover: (index: number) => void;
  style?: CSSProperties;
}) {
  const rows = Array.isArray(options) ? options : [];
  if (!rows.length) return null;
  const meta = {
    calendar: { label: "System", Icon: CalendarDays },
    field: { label: "Input", Icon: Braces },
    sentence: { label: "Sentence", Icon: TextQuote },
  } as const;

  return (
    <div className="absolute z-30 w-64 overflow-hidden rounded-md border border-line bg-elevated shadow-xl" style={style}>
      <div className="flex items-center justify-between border-b border-line bg-soft px-3 py-2">
        <span className="font-mono text-[10px] font-bold uppercase text-ink-3">Insert token</span>
        <span className="font-mono text-[10px] text-ink-3">{rows.length}</span>
      </div>
      <div className="max-h-64 overflow-y-auto p-1">
        {rows.map((option, index) => {
          const kind = option.kind === "calendar" || option.kind === "sentence" ? option.kind : "field";
          const Icon = meta[kind].Icon;
          return (
            <button
              key={option.key || `${kind}-${option.label}-${index}`}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                onSelect(option);
              }}
              onMouseEnter={() => onHover(index)}
              className={cn(
                "flex w-full items-center gap-2 rounded px-2.5 py-2 text-left",
                index === selectedIndex ? "bg-brand text-white" : "text-ink hover:bg-soft",
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-bold">{option.label}</span>
                {option.description && <span className={cn("block truncate text-[10px]", index === selectedIndex ? "text-white/80" : "text-ink-3")}>{option.description}</span>}
              </span>
              <span className={cn("font-mono text-[9px] font-bold uppercase", index === selectedIndex ? "text-white/80" : "text-ink-3")}>{meta[kind].label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SentenceLibrary({
  items,
  fields,
  onChange,
}: {
  items: StudioSentence[];
  fields: StudioCallField[];
  onChange: (items: StudioSentence[]) => void;
}) {
  const textareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
  const [mention, setMention] = useState<{ row: number; cursor: number } | null>(null);
  const mentionFields = useMemo(
    () => fields.map((field) => field.fieldName.trim()).filter(Boolean),
    [fields],
  );

  const update = (index: number, patch: Partial<StudioSentence>) => {
    onChange(items.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  };

  const insertMention = (fieldName: string) => {
    if (!mention) return;
    const current = items[mention.row]?.sentence || "";
    const before = current.slice(0, Math.max(0, mention.cursor - 1));
    const after = current.slice(mention.cursor);
    const inserted = `{{${fieldName}}}`;
    update(mention.row, { sentence: `${before}${inserted}${after}` });
    const row = mention.row;
    setMention(null);
    requestAnimationFrame(() => {
      const textarea = textareaRefs.current[row];
      const cursor = before.length + inserted.length;
      textarea?.focus();
      textarea?.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold text-ink">Sentence library</h2>
          <p className="mt-1 text-xs text-ink-3">
            Reusable responses referenced in prompts with <span className="font-mono">{"{{sentence_name}}"}</span>.
          </p>
        </div>
        <button
          type="button"
          disabled={items.length >= 50}
          onClick={() => onChange([...items, { name: "", sentence: "" }])}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-ink px-3 text-xs font-bold text-canvas hover:bg-ink-2 disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" /> Add sentence
        </button>
      </div>

      {items.length === 0 ? (
        <button
          type="button"
          onClick={() => onChange([{ name: "", sentence: "" }])}
          className="flex min-h-44 w-full flex-col items-center justify-center rounded-md border border-dashed border-line bg-soft text-center hover:border-ink-3"
        >
          <Plus className="h-5 w-5 text-ink-3" />
          <span className="mt-3 text-xs font-bold text-ink">Create the first reusable sentence</span>
          <span className="mt-1 text-[11px] text-ink-3">Up to 50 entries. Type @ to insert a call infield.</span>
        </button>
      ) : (
        <div className="grid gap-3">
          {items.map((item, index) => (
            <article key={index} className="rounded-md border border-line bg-soft p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] font-bold uppercase text-ink-3">
                  Sentence {index + 1}
                </span>
                <div className="flex items-center">
                  <IconButton label="Move up" disabled={index === 0} onClick={() => onChange(moveItem(items, index, index - 1))}>
                    <ChevronUp className="h-3.5 w-3.5" />
                  </IconButton>
                  <IconButton label="Move down" disabled={index === items.length - 1} onClick={() => onChange(moveItem(items, index, index + 1))}>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </IconButton>
                  <IconButton label="Remove sentence" danger onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[minmax(180px,0.35fr)_minmax(0,1fr)]">
                <label>
                  <span className="mb-1.5 block text-[11px] font-bold text-ink-2">Reference name</span>
                  <input
                    value={item.name}
                    onChange={(event) => update(index, { name: event.target.value.replace(/\s+/g, "_") })}
                    placeholder="welcome_sentence"
                    className="h-10 w-full rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand"
                  />
                </label>
                <label className="relative">
                  <span className="mb-1.5 flex items-center justify-between text-[11px] font-bold text-ink-2">
                    Sentence <span className="font-mono font-normal text-ink-3">{item.sentence.length}/4000</span>
                  </span>
                  <textarea
                    ref={(node) => { textareaRefs.current[index] = node; }}
                    rows={2}
                    maxLength={4000}
                    value={item.sentence}
                    onChange={(event) => {
                      const cursor = event.target.selectionStart || 0;
                      update(index, { sentence: event.target.value });
                      setMention(
                        event.target.value.slice(0, cursor).endsWith("@") && mentionFields.length
                          ? { row: index, cursor }
                          : null,
                      );
                    }}
                    placeholder="The exact reusable wording."
                    className="min-h-20 w-full resize-y rounded-md border border-line bg-elevated px-3 py-2 text-sm leading-5 outline-none focus:border-brand"
                  />
                  {mention?.row === index && (
                    <div className="absolute bottom-3 left-2 z-20 max-h-44 min-w-52 overflow-y-auto rounded-md border border-line bg-elevated p-1 shadow-xl">
                      {mentionFields.map((fieldName) => (
                        <button
                          key={fieldName}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => insertMention(fieldName)}
                          className="block w-full rounded px-3 py-2 text-left font-mono text-xs text-ink hover:bg-soft"
                        >
                          {`{{${fieldName}}}`}
                        </button>
                      ))}
                    </div>
                  )}
                </label>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function normalizeHeader(value: unknown) {
  return String(value || "").trim().toLowerCase().replace(/[^\w]+/g, " ");
}

function ValidValuesInput({ values = [], onChange }: { values: StudioValidValue[], onChange: (values: StudioValidValue[]) => void }) {
  const [inputValue, setInputValue] = useState("");

  const addValue = (val: string) => {
    const trimmed = val.trim();
    if (trimmed && !values.some((v) => v.value === trimmed)) {
      onChange([...values, { value: trimmed, description: "" }]);
    }
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addValue(inputValue);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.includes(",")) {
      const parts = val.split(",").map((p) => p.trim()).filter(Boolean);
      const newValues = [...values];
      let added = false;
      parts.forEach((p) => {
        if (!newValues.some((v) => v.value === p)) {
          newValues.push({ value: p, description: "" });
          added = true;
        }
      });
      if (added) {
        onChange(newValues);
      }
      setInputValue("");
    } else {
      setInputValue(val);
    }
  };

  const removeValue = (indexToRemove: number) => {
    onChange(values.filter((_, idx) => idx !== indexToRemove));
  };

  const updateDescription = (index: number, desc: string) => {
    const next = [...values];
    next[index] = { ...next[index], description: desc };
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {values.map((val, vIndex) => (
        <div key={vIndex} className="rounded-md border border-line bg-elevated p-2">
          <div className="flex items-center justify-between mb-2">
            <span className="inline-flex items-center rounded-md bg-brand-soft/50 px-2 py-0.5 text-[11px] font-bold text-brand">
              {val.value}
            </span>
            <button
              type="button"
              title="Remove valid value"
              onClick={() => removeValue(vIndex)}
              className="text-ink-3 hover:text-danger"
            >
              <MinusCircle className="h-3.5 w-3.5" />
            </button>
          </div>
          <input
            value={val.description || ""}
            onChange={(event) => updateDescription(vIndex, event.target.value)}
            className="h-8 w-full rounded-md border border-line bg-soft px-3 text-xs outline-none focus:border-brand"
            placeholder="Description (optional)"
          />
        </div>
      ))}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Add a valid value (press Enter or comma)..."
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (inputValue.trim()) {
              addValue(inputValue);
            }
          }}
          className="h-9 flex-1 rounded-md border border-line bg-elevated px-3 text-xs outline-none focus:border-brand"
        />
        <button
          type="button"
          className="inline-flex h-9 items-center rounded-md bg-soft px-3 text-xs font-bold text-ink hover:bg-line"
          onClick={() => addValue(inputValue)}
        >
          Add
        </button>
      </div>
    </div>
  );
}

function RetryValuesInput({ values = [], onChange }: { values: string[], onChange: (values: string[]) => void }) {
  const [inputValue, setInputValue] = useState("");

  const commit = (raw: string) => {
    const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...values];
    let added = false;
    parts.forEach((part) => {
      if (!next.some((v) => v.toLowerCase() === part.toLowerCase())) {
        next.push(part);
        added = true;
      }
    });
    if (added) onChange(next);
    setInputValue("");
  };

  const removeValue = (indexToRemove: number) => {
    onChange(values.filter((_, idx) => idx !== indexToRemove));
  };

  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((value, index) => (
            <span key={index} className="inline-flex items-center gap-1 rounded-md bg-brand-soft/50 px-2 py-1 text-[11px] font-bold text-brand">
              {value}
              <button type="button" title="Remove value" onClick={() => removeValue(index)} className="text-brand/70 hover:text-danger">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="e.g. Busy, Yes (press Enter or comma)..."
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault();
              commit(inputValue);
            }
          }}
          onBlur={() => inputValue.trim() && commit(inputValue)}
          className="h-9 flex-1 rounded-md border border-line bg-elevated px-3 text-xs outline-none focus:border-brand"
        />
        <button
          type="button"
          className="inline-flex h-9 items-center rounded-md bg-soft px-3 text-xs font-bold text-ink hover:bg-line"
          onClick={() => commit(inputValue)}
        >
          Add
        </button>
      </div>
    </div>
  );
}

function SuccessValuesInput({ values = [], onChange }: { values: string[]; onChange: (values: string[]) => void }) {
  const [inputValue, setInputValue] = useState("");

  const commit = (raw: string) => {
    const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...values];
    let added = false;
    parts.forEach((part) => {
      if (!next.some((v) => v.toLowerCase() === part.toLowerCase())) {
        next.push(part);
        added = true;
      }
    });
    if (added) onChange(next);
    setInputValue("");
  };

  const removeValue = (indexToRemove: number) => {
    onChange(values.filter((_, idx) => idx !== indexToRemove));
  };

  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((value, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-100 px-2 py-1 text-[11px] font-bold text-amber-900 dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            >
              {value}
              <button
                type="button"
                title="Remove value"
                onClick={() => removeValue(index)}
                className="text-amber-700 hover:text-danger dark:text-amber-300"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder='e.g. "Appointment Booked" (press Enter or comma)...'
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault();
              commit(inputValue);
            }
          }}
          onBlur={() => inputValue.trim() && commit(inputValue)}
          className="h-9 flex-1 rounded-md border border-amber-300 bg-elevated px-3 text-xs outline-none focus:border-amber-500 dark:border-amber-700"
        />
        <button
          type="button"
          className="inline-flex h-9 items-center rounded-md border border-amber-300 bg-amber-100 px-3 text-xs font-bold text-amber-900 hover:bg-amber-200 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
          onClick={() => commit(inputValue)}
        >
          Add
        </button>
      </div>
    </div>
  );
}

function NormaliseMultiSelect({
  item,
  onPatch,
  languages,
}: {
  item: StudioCallField;
  onPatch: (patch: Partial<StudioCallField>) => void;
  languages: string[];
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open ]);

  const picked: string[] = [];
  if (item.normalise === true) picked.push("Normalise");
  if (item.emphasis === true) picked.push("Emphasis");
  if (item.to_lowercase === true) picked.push("Lowercase");
  if (item.number_to_words === true) picked.push("Number to Words");
  const lang = item.infield_language || "NA";
  const summary = picked.length ? picked.join(" + ") + (item.normalise === true && lang !== "NA" ? ` · ${lang}` : "") : "Off";

  const toggleNormalise = (enabled: boolean) => {
    onPatch(enabled
      ? { normalise: true }
      : { normalise: false, emphasis: false, to_lowercase: false, number_to_words: false, infield_language: "NA" });
  };

  const toggleNumberToWords = (enabled: boolean) => {
    onPatch({
      number_to_words: enabled,
      ...(enabled && (!item.infield_language || item.infield_language === "NA") ? { infield_language: "English" } : {}),
    });
  };

  const optionRow = (
    label: string,
    hint: string,
    checked: boolean,
    disabled: boolean,
    onToggle: (next: boolean) => void,
  ) => (
    <label className={cn("flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-2 hover:bg-soft", disabled && "cursor-not-allowed opacity-50 hover:bg-transparent")}>
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--brand,#7c3aed)]"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onToggle(event.target.checked)}
      />
      <span className="min-w-0">
        <span className="block text-xs font-bold text-ink-2">{label}</span>
        <span className="block truncate text-[11px] font-normal text-ink-3" title={hint}>{hint}</span>
      </span>
    </label>
  );

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn("flex h-9 w-full items-center justify-between gap-2 rounded-md border border-line bg-elevated px-3 text-xs outline-none focus:border-brand", !picked.length && "text-ink-3")}
      >
        <span className="min-w-0 flex-1 truncate text-left font-semibold">{summary}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-ink-3 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded-md border border-line bg-elevated p-1.5 shadow-lg sm:right-auto sm:w-72">
          {optionRow("Normalise", "Rewrite values via LLM", item.normalise === true, false, toggleNormalise)}
          {optionRow("Emphasis", "Comma-separated spoken tokens", item.emphasis === true, item.normalise !== true, (next) => onPatch({ emphasis: next }))}
          {optionRow("Lowercase", "Lowercase first, then normalise", item.to_lowercase === true, item.normalise !== true, (next) => onPatch({ to_lowercase: next }))}
          {optionRow("Number to Words", "Spell out numbers (lakh/crore) or dates as words", item.number_to_words === true, item.normalise !== true, toggleNumberToWords)}
          <div className="mt-1 border-t border-line px-2 pb-1 pt-2">
            <span className="mb-1.5 block text-[11px] font-bold text-ink-2">Language</span>
            <select
              disabled={item.normalise !== true}
              value={lang}
              onChange={(event) => onPatch({ infield_language: event.target.value })}
              className="h-9 w-full rounded-md border border-line bg-soft px-2 text-xs outline-none disabled:opacity-50"
            >
              <option value="NA">Default language</option>
              {languages.map((language) => <option key={language} value={language}>{language}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export function PostCallFieldEditor({  label,
  items,
  onChange,
  infieldLanguages = [],
  maxDescriptionLength,
  singleWinner = false,
  onSingleWinnerChange,
  primaryOutcome = "",
  onPrimaryOutcomeChange,
  findings = [],
  showOldFindings = false,
}: {
  /** Outcomes only: findings from the latest outcome check, shown under each row. */
  findings?: OutcomeFinding[];
  /** Outcomes only: also show issues of old, untouched outcomes. */
  showOldFindings?: boolean;
  label: "Call infields" | "Call outcomes";
  items: StudioCallField[];
  onChange: (items: StudioCallField[]) => void;
  infieldLanguages?: string[];
  maxDescriptionLength: number;
  // Outcomes only: "Only one outcome per call" toggle.
  singleWinner?: boolean;
  onSingleWinnerChange?: (next: boolean) => void;
  /** Outcomes only: fieldName of the current primary outcome (empty = none). */
  primaryOutcome?: string;
  /** Outcomes only: called when the user selects / deselects a primary outcome. */
  onPrimaryOutcomeChange?: (fieldName: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isInfield = label === "Call infields";
  const singular = isInfield ? "infield" : "outcome";
  // Outcome rows whose (optional) default-value field the user has opened but
  // not yet filled in. A row with a saved value renders it without this.
  const [openDefaultValueRows, setOpenDefaultValueRows] = useState<Set<number>>(new Set());
  // Outcome rows whose (optional) retry-values section the user has opened but
  // not yet filled in. A row with saved retry values renders it without this.
  const [openRetryRows, setOpenRetryRows] = useState<Set<number>>(new Set());

  const setDefaultValueOpen = (index: number, open: boolean) => {
    setOpenDefaultValueRows((current) => {
      const next = new Set(current);
      if (open) next.add(index); else next.delete(index);
      return next;
    });
  };

  const setRetryOpen = (index: number, open: boolean) => {
    setOpenRetryRows((current) => {
      const next = new Set(current);
      if (open) next.add(index); else next.delete(index);
      return next;
    });
  };

  const update = (index: number, patch: Partial<StudioCallField>) => {
    onChange(items.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  };

  // Moving/removing a row shifts every index after it, so drop the open-but-empty
  // flags rather than letting them point at the wrong row. Rows with a saved
  // default value render it regardless of this set.
  const reorder = (next: StudioCallField[]) => {
    setOpenDefaultValueRows(new Set());
    setOpenRetryRows(new Set());
    onChange(next);
  };

  const importFile = async (file: File) => {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
    const standardImported = rows.map<StudioCallField | null>((row) => {
      const values = new Map(Object.entries(row).map(([key, value]) => [normalizeHeader(key), value]));
      const get = (...keys: string[]) => {
        for (const key of keys) {
          const value = values.get(normalizeHeader(key));
          if (value !== undefined) return String(value).trim();
        }
        return "";
      };
      const fieldName = get(`${singular} name`, "field name", "name");
      if (!fieldName) return null;
      const rawType = get(`${singular} type`, "field type", "type").toLowerCase();
      return {
        fieldName,
        fieldType: FIELD_TYPES.includes(rawType) ? rawType : "string",
        visible: true,
        description: get("description").slice(0, maxDescriptionLength),
        validValues: get("valid values", "validvalues").split(",").map((value) => value.trim()).filter(Boolean).map((value) => ({ value })),
        ...(isInfield
          ? { infield_language: "NA" }
          : { defaultValue: get("default value", "defaultvalue").slice(0, 200) }),
      };
    }).filter((item): item is StudioCallField => item !== null);

    let imported = standardImported;

    // Fallback: Column-based format (if no "Name" column was found)
    if (imported.length === 0 && rows.length > 0) {
      const headers = Object.keys(rows[0]);
      const columnBasedImported: StudioCallField[] = [];
      const processedHeaders = new Set<string>();

      for (const header of headers) {
        if (processedHeaders.has(header)) continue;
        if (header.toLowerCase().endsWith(" description") || header.toLowerCase().endsWith(" type")) continue;

        processedHeaders.add(header);
        const validValuesSet = new Set<string>();
        for (const row of rows) {
          const val = row[header];
          if (val !== undefined && val !== "") validValuesSet.add(String(val).trim());
        }

        const descHeader = headers.find((h) => h.toLowerCase() === `${header.toLowerCase()} description`);
        let description = "";
        if (descHeader) {
          const rowWithDesc = rows.find((r) => r[descHeader]);
          if (rowWithDesc) description = String(rowWithDesc[descHeader]).trim().slice(0, maxDescriptionLength);
        }

        const typeHeader = headers.find((h) => h.toLowerCase() === `${header.toLowerCase()} type`);
        let fieldType = validValuesSet.size > 0 ? "multi-select" : "string";
        if (typeHeader) {
          const rowWithType = rows.find((r) => r[typeHeader]);
          if (rowWithType) {
            const t = String(rowWithType[typeHeader]).trim().toLowerCase();
            if (FIELD_TYPES.includes(t)) fieldType = t;
          }
        }

        columnBasedImported.push({
          fieldName: header,
          fieldType,
          visible: true,
          description,
          validValues: Array.from(validValuesSet).map((v) => ({ value: v })),
          ...(isInfield ? { infield_language: "NA" } : {}),
        });
      }
      imported = columnBasedImported;
    }
    onChange([...items, ...imported].slice(0, 25));
  };

  return (
    <div className="space-y-4">
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void importFile(file);
        }}
      />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold text-ink">{label}</h2>
          <p className="mt-1 text-xs text-ink-3">
            {isInfield ? "Data supplied before a live call." : "Structured values extracted after a call."}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-elevated px-3 text-xs font-bold text-ink hover:bg-soft"
            >
              <FileUp className="h-3.5 w-3.5" /> Import
            </button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="p-1 outline-none">
                    <HelpCircle className="h-4 w-4 text-ink-3 hover:text-ink transition-colors" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" align="center" className="max-w-xs bg-ink text-canvas">
                  <div className="text-[11px] p-1">
                    <p><strong>Required Headers:</strong></p>
                    <ul className="mt-1 space-y-0.5 text-ink-3">
                      <li>• <code>Name</code></li>
                      <li>• <code>Description</code></li>
                      <li>• <code>Type</code> (e.g., multi-select)</li>
                      <li>• <code>Valid values</code> (comma-separated)</li>
                    </ul>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <button
            type="button"
            disabled={items.length >= 25}
            onClick={() => onChange([...items, {
              fieldName: "",
              fieldType: "string",
              visible: true,
              description: "",
              validValues: [],
              ...(isInfield ? { infield_language: "NA" } : { defaultValue: "" }),
            }])}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-ink px-3 text-xs font-bold text-canvas hover:bg-ink-2 disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      </div>

      {!isInfield && onSingleWinnerChange && (
        <div className="mb-3 rounded-md border border-line bg-soft px-3 py-2.5" data-testid="outcome-single-winner">
          <div className="flex items-center justify-between gap-3 text-xs font-bold text-ink-2">
            Only one outcome per call
            <ToggleSwitch checked={singleWinner} onChange={onSingleWinnerChange} ariaLabel="Only one outcome per call" />
          </div>
          {singleWinner && (
            <p className="mt-1.5 text-[11px] font-normal text-ink-3">
              Give competing outcomes a Group and a Priority (1 = highest). If several groups
              match, only the top priority is kept and the rest become NA. Same group name pairs
              fields. No priority or a tie falls back to list order. Outcomes without a group are
              never cleared.
            </p>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="flex min-h-32 items-center justify-center rounded-md border border-dashed border-line bg-soft text-xs text-ink-3">
          No {label.toLowerCase()} configured.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((item, index) => {
            const isPrimary = !isInfield && Boolean(item.fieldName) && item.fieldName.trim() === primaryOutcome;
            return (
            <article key={index} className={cn("rounded-md border bg-soft p-4", isPrimary ? "border-amber-400 dark:border-amber-500" : "border-line")}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] font-bold uppercase text-ink-3">{singular} {index + 1}</span>
                  {!isInfield && onPrimaryOutcomeChange && (
                    <button
                      type="button"
                      title={isPrimary ? "Remove as primary outcome" : "Set as primary outcome"}
                      aria-label={isPrimary ? "Remove as primary outcome" : "Set as primary outcome"}
                      onClick={() => {
                        if (!item.fieldName.trim()) return;
                        onPrimaryOutcomeChange(isPrimary ? "" : item.fieldName.trim());
                      }}
                      className={cn(
                        "flex h-6 w-6 items-center justify-center rounded-md transition-colors",
                        isPrimary
                          ? "text-amber-500 hover:text-amber-600"
                          : "text-ink-faint hover:text-amber-400"
                      )}
                    >
                      <Star className={cn("h-3.5 w-3.5", isPrimary && "fill-current")} />
                    </button>
                  )}
                  {isPrimary && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                      Primary
                    </span>
                  )}
                </div>
                <div className="flex">
                  <IconButton label="Move up" disabled={index === 0} onClick={() => reorder(moveItem(items, index, index - 1))}><ChevronUp className="h-3.5 w-3.5" /></IconButton>
                  <IconButton label="Move down" disabled={index === items.length - 1} onClick={() => reorder(moveItem(items, index, index + 1))}><ChevronDown className="h-3.5 w-3.5" /></IconButton>
                  <IconButton label={`Remove ${singular}`} danger onClick={() => reorder(items.filter((_, itemIndex) => itemIndex !== index))}><Trash2 className="h-3.5 w-3.5" /></IconButton>
                </div>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-[11px] font-bold text-ink-2">Field name</span>
                  <input value={item.fieldName} onChange={(event) => update(index, { fieldName: event.target.value })} className="h-10 w-full rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand" />
                </label>
                <label>
                  <span className="mb-1.5 block text-[11px] font-bold text-ink-2">Type</span>
                  <select value={item.fieldType || "string"} onChange={(event) => update(index, { fieldType: event.target.value })} className="h-10 w-full rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand">
                    {FIELD_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                </label>
              </div>
              {!isInfield && singleWinner && (
                <div className={cn("mt-3 grid gap-3", String(item.exclusive_group || "").trim() && "sm:grid-cols-[1fr_7rem]")}>
                  <label className="block">
                    <span className="mb-1.5 block text-[11px] font-bold text-ink-2">Group</span>
                    <input
                      value={item.exclusive_group || ""}
                      maxLength={120}
                      onChange={(event) => update(index, { exclusive_group: event.target.value })}
                      placeholder="Empty = independent. Same name = paired"
                      className="h-10 w-full rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand"
                    />
                  </label>
                  {String(item.exclusive_group || "").trim() && (
                    <label className="block">
                      <span className="mb-1.5 block text-[11px] font-bold text-ink-2">Priority</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={item.outcome_priority ?? ""}
                        onChange={(event) => {
                          const next = Number.parseInt(event.target.value, 10);
                          update(index, { outcome_priority: Number.isFinite(next) && next >= 1 ? next : null });
                        }}
                        placeholder="1 = top"
                        className="h-10 w-full rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand"
                      />
                    </label>
                  )}
                </div>
              )}
              {item.fieldType === "multi-select" && (
                <div className="mt-3 block">
                  <span className="mb-1.5 block text-[11px] font-bold text-ink-2">Valid values</span>
                  <ValidValuesInput
                    values={item.validValues || []}
                    onChange={(newValues) => update(index, { validValues: newValues })}
                  />
                </div>
              )}
              {!isInfield && usesFourBoxes(item) ? (
                <OutcomeSectionsEditor
                  idPrefix={`outcome-${index}`}
                  value={{ ...EMPTY_SECTIONS, ...(item.sections || {}) }}
                  onChange={(sections) => update(index, { sections, description: compileSections(sections) })}
                />
              ) : (
                <>
                  <label className="mt-3 block">
                    <span className="mb-1.5 flex justify-between text-[11px] font-bold text-ink-2">
                      Description <span className="font-mono font-normal text-ink-3">{String(item.description || "").length}/{maxDescriptionLength}</span>
                    </span>
                    <textarea rows={2} maxLength={maxDescriptionLength} value={item.description || ""} onChange={(event) => update(index, { description: event.target.value })} placeholder="Describe what this outcome tracks..." className="min-h-20 w-full resize-y rounded-md border border-line bg-elevated px-3 py-2 text-sm outline-none focus:border-brand" />
                  </label>
                  {!isInfield && (
                    <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-ink-3" data-testid="outcome-old-format-note">
                      <span title="Old text outcomes are not changed unless you switch.">Old text format, not changed unless you switch.</span>
                      <button
                        type="button"
                        title="Moves your text into Rules for you to split into the four boxes."
                        onClick={() => update(index, switchToFourBoxes(item))}
                        className="shrink-0 font-bold text-brand hover:underline"
                      >
                        Switch to four boxes
                      </button>
                    </div>
                  )}
                </>
              )}
              {!isInfield && item.fieldType !== "multi-select" && item.fieldType !== "boolean" && (
                <label className="mt-2 flex items-center gap-2 text-[11px] text-ink-2">
                  <input
                    type="checkbox"
                    checked={item.freeText === true}
                    onChange={(event) => update(index, { freeText: event.target.checked })}
                  />
                  Free text: this outcome has no fixed list of values
                </label>
              )}
              {!isInfield && <InlineFindings showOld={showOldFindings} findings={findings.filter((finding) => finding.field_name === item.fieldName)} />}
              {isInfield && (
                <div className="mt-3 border-t border-line pt-3">
                  <span className="mb-1.5 block text-[11px] font-bold text-ink-2">Normalization</span>
                  <NormaliseMultiSelect item={item} onPatch={(patch) => update(index, patch)} languages={infieldLanguages} />
                </div>
              )}
              {!isInfield && (item.defaultValue || openDefaultValueRows.has(index) ? (
                <div className="mt-3 border-t border-line pt-3">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[11px] font-bold text-ink-2">Default value</span>
                    <button
                      type="button"
                      onClick={() => {
                        update(index, { defaultValue: "" });
                        setDefaultValueOpen(index, false);
                      }}
                      className="text-[11px] font-bold text-ink-3 hover:text-danger"
                    >
                      Remove
                    </button>
                  </div>
                  <input
                    value={item.defaultValue || ""}
                    maxLength={200}
                    autoFocus={!item.defaultValue}
                    onChange={(event) => update(index, { defaultValue: event.target.value })}
                    placeholder="Leave blank to keep the built-in default"
                    className="h-10 w-full rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand"
                  />
                  <span className="mt-1.5 block text-[11px] text-ink-3">
                    Written for this outcome when a call is finalized without LLM analysis
                    (Short Call Retry / LLM Skip). Blank falls back to &quot;Hang up&quot; on the
                    call-back field and &quot;NA&quot; on the rest.
                  </span>
                </div>
              ) : null)}
              {!isInfield && ((item.retryValues && item.retryValues.length > 0) || openRetryRows.has(index) ? (
                <div className="mt-3 border-t border-line pt-3">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[11px] font-bold text-ink-2">Retry on outcome value</span>
                    <button
                      type="button"
                      onClick={() => {
                        update(index, { retryValues: [] });
                        setRetryOpen(index, false);
                      }}
                      className="text-[11px] font-bold text-ink-3 hover:text-danger"
                    >
                      Remove
                    </button>
                  </div>
                  <RetryValuesInput
                    values={item.retryValues || []}
                    onChange={(newValues) => update(index, { retryValues: newValues })}
                  />
                </div>
              ) : null)}
              {isPrimary && (
                <div className="mt-3 rounded-md border border-amber-300/80 bg-amber-50/70 p-3 dark:border-amber-700/50 dark:bg-amber-950/30">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[11px] font-bold text-amber-900 dark:text-amber-200">
                      Success values (Sub-dispositions)
                      <span className="ml-1 font-normal text-amber-700 dark:text-amber-400">(optional)</span>
                    </span>
                    <span className="text-[10px] text-amber-700 dark:text-amber-400">
                      Only these values count as a KPI hit
                    </span>
                  </div>
                  <p className="mb-2 text-[10px] text-amber-800/80 dark:text-amber-300/80">
                    Leave empty to count any positive/non-empty outcome. Add specific sub-disposition values (e.g. &ldquo;Appointment Booked&rdquo;) to count only calls with those values.
                  </p>
                  <SuccessValuesInput
                    values={item.successValues || []}
                    onChange={(newValues) => update(index, { successValues: newValues })}
                  />
                </div>
              )}
              {!isInfield && (() => {
                const showDefault = !(item.defaultValue || openDefaultValueRows.has(index));
                const showRetry = !((item.retryValues && item.retryValues.length > 0) || openRetryRows.has(index));
                if (!showDefault && !showRetry) return null;
                return (
                  <div className={cn("mt-3 grid gap-2", showDefault && showRetry ? "grid-cols-2" : "grid-cols-1 justify-items-start")}>
                    {showDefault && (
                      <button
                        type="button"
                        onClick={() => setDefaultValueOpen(index, true)}
                        className="inline-flex h-8 min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-line bg-elevated px-2 text-[11px] font-bold text-ink-2 hover:bg-soft"
                      >
                        <Plus className="h-3 w-3" /> Add default value
                      </button>
                    )}
                    {showRetry && (
                      <button
                        type="button"
                        onClick={() => setRetryOpen(index, true)}
                        className="inline-flex h-8 min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-line bg-elevated px-2 text-[11px] font-bold text-ink-2 hover:bg-soft"
                      >
                        <Plus className="h-3 w-3" /> Add retry on value
                      </button>
                    )}
                  </div>
                );
              })()}
            </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function destinationsToText(destinations: Record<string, string>) {
  return Object.entries(destinations).map(([label, number]) => `${label}: ${number}`).join("\n");
}

function textToDestinations(value: string) {
  return Object.fromEntries(
    value.split("\n").map((line) => {
      const separator = line.indexOf(":");
      return separator < 0 ? ["", ""] : [line.slice(0, separator).trim(), line.slice(separator + 1).trim()];
    }).filter(([label, number]) => Boolean(label && number)),
  );
}

export function TransferEditor({
  value,
  onChange,
}: {
  value: StudioTransferConfig | null;
  onChange: (value: StudioTransferConfig | null) => void;
}) {
  const configured = value !== null;
  const config = value || DEFAULT_TRANSFER;
  const [destinationsText, setDestinationsText] = useState(() => destinationsToText(config.destinations));
  const update = (patch: Partial<StudioTransferConfig>) => onChange({ ...config, ...patch });
  const labels = Object.keys(config.destinations);

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-bold text-ink">Call transfer</h2>
          <p className="mt-1 text-xs leading-5 text-ink-3">Version-level routing to a human or specialist.</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={configured}
          onClick={() => onChange(configured ? null : { ...DEFAULT_TRANSFER })}
          className={cn("relative h-5 w-9 rounded-full transition-colors", configured ? "bg-brand" : "bg-line")}
        >
          <span className={cn("absolute top-0.5 h-4 w-4 rounded-full transition-all", configured ? "left-[18px] bg-white" : "left-0.5 bg-white")} />
        </button>
      </div>
      {configured && (
        <div className="mt-5 grid gap-4 border-t border-line pt-5 md:grid-cols-2">
          <label className="flex items-center justify-between rounded-md border border-line bg-soft px-3 py-2.5 text-xs font-bold text-ink">
            Transfer enabled
            <input type="checkbox" checked={config.enabled} onChange={(event) => update({ enabled: event.target.checked })} />
          </label>
          <label className="md:col-span-2">
            <span className="mb-1.5 block text-[11px] font-bold text-ink-2">Destinations, one label and number per line</span>
            <textarea
              rows={3}
              value={destinationsText}
              onChange={(event) => {
                setDestinationsText(event.target.value);
                update({ destinations: textToDestinations(event.target.value) });
              }}
              placeholder={"sales: +911234567890\nsupport: +919999900000"}
              className="w-full resize-y rounded-md border border-line bg-soft px-3 py-2 font-mono text-xs outline-none focus:border-brand"
            />
          </label>
          <TransferTrigger
            title="Time limit fallback"
            enabled={config.on_time_limit_enabled}
            label={config.on_time_limit_label}
            message={config.on_time_limit_message}
            labels={labels}
            onChange={(patch) => update({
              on_time_limit_enabled: patch.enabled,
              on_time_limit_label: patch.label,
              on_time_limit_message: patch.message,
            })}
          />
          <TransferTrigger
            title="Question threshold"
            enabled={config.after_questions_enabled}
            label={config.after_questions_label}
            message={config.after_questions_message}
            labels={labels}
            count={config.after_questions_count}
            onChange={(patch) => update({
              after_questions_enabled: patch.enabled,
              after_questions_label: patch.label,
              after_questions_message: patch.message,
              after_questions_count: patch.count ?? config.after_questions_count,
            })}
          />
        </div>
      )}
    </div>
  );
}

function TransferTrigger({
  title,
  enabled,
  label,
  message,
  labels,
  count,
  onChange,
}: {
  title: string;
  enabled: boolean;
  label: string;
  message: string;
  labels: string[];
  count?: number;
  onChange: (value: { enabled: boolean; label: string; message: string; count?: number }) => void;
}) {
  return (
    <div className="space-y-3 rounded-md border border-line bg-soft p-4">
      <label className="flex items-center justify-between text-xs font-bold text-ink">
        {title}
        <input type="checkbox" checked={enabled} onChange={(event) => onChange({ enabled: event.target.checked, label, message, count })} />
      </label>
      {count !== undefined && (
        <input type="number" min={1} value={count} onChange={(event) => onChange({ enabled, label, message, count: Number(event.target.value) || 1 })} className="h-9 w-full rounded-md border border-line bg-elevated px-3 text-xs outline-none" />
      )}
      <select value={label} onChange={(event) => onChange({ enabled, label: event.target.value, message, count })} className="h-9 w-full rounded-md border border-line bg-elevated px-3 text-xs outline-none">
        <option value="">Select destination</option>
        {labels.map((item) => <option key={item} value={item}>{item}</option>)}
      </select>
      <input value={message} onChange={(event) => onChange({ enabled, label, message: event.target.value, count })} placeholder="Message spoken before transfer" className="h-9 w-full rounded-md border border-line bg-elevated px-3 text-xs outline-none" />
    </div>
  );
}

export function PromptInspectionModal({
  title,
  prompt,
  onClose,
}: {
  title: string;
  prompt: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
      <div className="flex h-[min(88vh,900px)] w-full max-w-6xl flex-col overflow-hidden rounded-md border border-line bg-elevated shadow-2xl">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-line px-5">
          <div>
            <h2 className="text-sm font-bold text-ink">{title}</h2>
            <p className="font-mono text-[10px] text-ink-3">{prompt.length.toLocaleString()} characters</p>
          </div>
          <IconButton label="Close prompt inspection" onClick={onClose}><X className="h-4 w-4" /></IconButton>
        </div>
        <PromptMarkdown
          content={prompt}
          ariaLabel={`${title} markdown preview`}
          emptyMessage="No system prompt has been entered."
          className="min-h-0 flex-1 rounded-none border-0 bg-canvas p-6"
        />
      </div>
    </div>
  );
}

function versionStatusClass(status?: string) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "published") return "bg-success-soft text-success";
  if (normalized === "draft") return "bg-warning-soft text-warning";
  return "bg-soft text-ink-2";
}

export function VersionHistory({

  versions,
  loading,
  onLoadDetails,
  onEdit,
  onDelete,
  onCreate,
  onRefresh,
}: {
  versions: StudioVersion[];
  loading: boolean;
  onLoadDetails: (versionId: string) => Promise<StudioVersion>;
  onEdit: (version: StudioVersion) => void;
  onDelete: (version: StudioVersion) => void;
  onCreate?: () => void;
  onRefresh?: () => void;
}) {
  const { user } = useAuth() as any;
  const userTz = user?.timezone;
  const [expandedId, setExpandedId] = useState("");
  const [details, setDetails] = useState<Record<string, StudioVersion>>({});
  const [loadingId, setLoadingId] = useState("");
  const [inspect, setInspect] = useState<StudioVersion | null>(null);
  const [copiedVersionId, setCopiedVersionId] = useState("");

  const detailFor = (version: StudioVersion) => details[version.version_id] || version;
  const ensureDetail = async (version: StudioVersion) => {
    if (!version.is_summary || details[version.version_id]) return detailFor(version);
    setLoadingId(version.version_id);
    try {
      const detail = await onLoadDetails(version.version_id);
      setDetails((current) => ({ ...current, [version.version_id]: detail }));
      return detail;
    } finally {
      setLoadingId("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold text-ink">Version history</h2>
          <p className="mt-1 text-xs text-ink-3">Published snapshots load their full content only when opened.</p>
        </div>
        <div className="flex gap-2">
          {onRefresh && (
            <button type="button" disabled={loading} onClick={onRefresh} className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-elevated px-3 text-xs font-bold text-ink hover:bg-soft">
              <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> Refresh
            </button>
          )}
          {onCreate && (
            <button type="button" onClick={onCreate} className="inline-flex h-9 items-center gap-2 rounded-md bg-ink px-3 text-xs font-bold text-canvas hover:bg-ink-2">
              <Plus className="h-3.5 w-3.5" /> Create version
            </button>
          )}
        </div>
      </div>
      <div className="overflow-hidden rounded-md border border-line">
        {versions.length === 0 ? (
          <div className="flex min-h-40 items-center justify-center bg-soft text-xs text-ink-3">No published versions yet.</div>
        ) : versions.map((version) => {
          const expanded = expandedId === version.version_id;
          const detail = detailFor(version);
          return (
            <article key={version.version_id} className="border-b border-line last:border-0">
              <button
                type="button"
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest("[data-copy-version-id]")) {
                    void navigator.clipboard.writeText(version.version_id);
                    setCopiedVersionId(version.version_id);
                    window.setTimeout(() => setCopiedVersionId((current) => current === version.version_id ? "" : current), 1500);
                    return;
                  }
                  setExpandedId(expanded ? "" : version.version_id);
                  if (!expanded) void ensureDetail(version);
                }}
                className="grid w-full grid-cols-[minmax(0,1fr)_100px_180px_150px] items-center gap-3 bg-elevated px-4 py-3 text-left hover:bg-soft"
              >
                <span className="flex min-w-0 items-center gap-2 text-xs font-bold text-ink">
                  {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0" />}
                  <span className="min-w-0">
                    <span className="block truncate">{version.version_name || version.version_id}</span>
                    {version.version_name && (
                      <span className="flex items-center gap-1 font-mono text-[10px] font-normal text-ink-3">
                        <span className="truncate" title={version.version_id}>{version.version_id}</span>
                        <span
                          data-copy-version-id
                          role="img"
                          aria-label={`Copy version ID ${version.version_id}`}
                          title="Copy version ID"
                          className={cn("shrink-0 rounded p-0.5 hover:bg-soft hover:text-ink", copiedVersionId === version.version_id && "text-success")}
                        >
                          {copiedVersionId === version.version_id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                        </span>
                      </span>
                    )}
                  </span>
                </span>
                <span className={cn("w-fit rounded px-2 py-1 text-[10px] font-bold uppercase", versionStatusClass(version.status))}>{version.status || "Saved"}</span>
                <span className="truncate font-mono text-[10px] text-ink-3">{version.published_at ? formatTimestamp(version.published_at, userTz) : "-"}</span>
                <span className="truncate text-xs text-ink-3">{version.created_by || "System"}</span>
              </button>
              {expanded && (
                <div className="bg-soft p-4">
                  {loadingId === version.version_id ? (
                    <div className="h-32 animate-pulse rounded-md bg-line/50" />
                  ) : (
                    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_220px]">
                      <div className="space-y-4">
                        <section>
                          <div className="mb-2 flex items-center justify-between">
                            <h3 className="font-mono text-[10px] font-bold uppercase text-ink-3">System prompt</h3>
                            <button type="button" onClick={() => setInspect(detail)} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-ink-2 hover:text-ink">
                              <Maximize2 className="h-3.5 w-3.5" /> Fullscreen
                            </button>
                          </div>
                          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-line bg-elevated p-4 font-mono text-xs leading-5 text-ink">{detail.system_prompt || "None provided"}</pre>
                        </section>
                        <section>
                          <h3 className="mb-2 font-mono text-[10px] font-bold uppercase text-ink-3">First message</h3>
                          <p className="rounded-md border border-line bg-elevated p-3 text-xs leading-5 text-ink">{detail.first_message || "None provided"}</p>
                        </section>
                        <div className="grid gap-3 sm:grid-cols-3">
                          <VersionMetric label="Tools" value={(detail.tool || detail.tools || []).length} />
                          <VersionMetric label="Infields" value={(detail.call_infields || []).length} />
                          <VersionMetric label="Outcomes" value={(detail.call_outcomes || []).length} />
                        </div>
                      </div>
                      <aside className="space-y-2 border-t border-line pt-4 xl:border-l xl:border-t-0 xl:pl-4 xl:pt-0">
                        <button type="button" onClick={() => void ensureDetail(version).then(onEdit)} className="h-9 w-full rounded-md border border-line bg-elevated px-3 text-left text-xs font-bold text-ink hover:bg-soft">Load for editing</button>
                        <button type="button" onClick={() => onDelete(version)} className="h-9 w-full rounded-md border border-danger/20 bg-elevated px-3 text-left text-xs font-bold text-danger hover:bg-danger-soft">Delete version</button>
                      </aside>
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
      {inspect && <PromptInspectionModal title={`${inspect.version_name || inspect.version_id} system prompt`} prompt={inspect.system_prompt || ""} onClose={() => setInspect(null)} />}
    </div>
  );
}

function VersionMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-line bg-elevated p-3">
      <p className="font-mono text-[10px] font-bold uppercase text-ink-3">{label}</p>
      <p className="mt-1 text-lg font-bold text-ink">{value}</p>
    </div>
  );
}

export function LanguagePreferenceFields({
  value,
  sttLanguage,
  onChange,
}: {
  value: LanguagePreference;
  sttLanguage: string;
  onChange: (value: LanguagePreference) => void;
}) {
  const languages = replyLanguagesForStt(sttLanguage);
  if (!languages.length) {
    return (
      <p className="mt-3 rounded-md border border-line bg-soft px-4 py-3 text-[11px] leading-5 text-ink-3">
        Primary and secondary languages need Hindi, English or Arabic transcription. With this
        transcription language, switching follows the standard rule.
      </p>
    );
  }
  const percent = value.languageSwitchPercent ?? DEFAULT_SWITCH_PERCENT;
  const choosePrimary = (primary: ReplyLanguage | null) => {
    if (!primary) {
      onChange(EMPTY_LANGUAGE_PREFERENCE);
      return;
    }
    const secondary = languages.find((language) => language !== primary) ?? null;
    onChange({ primaryLanguage: primary, secondaryLanguage: secondary, languageSwitchPercent: percent });
  };
  const selectClass = "h-10 w-full rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand";

  return (
    <div className="mt-3 rounded-md border border-line bg-soft px-4 py-3">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold text-ink">Primary language</span>
          <select
            value={value.primaryLanguage ?? ""}
            onChange={(event) => choosePrimary(readReplyLanguage(event.target.value))}
            className={selectClass}
          >
            <option value="">Not set (standard switching)</option>
            {languages.map((language) => (
              <option key={language} value={language}>{REPLY_LANGUAGE_LABELS[language]}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold text-ink">Secondary language</span>
          <select
            value={value.secondaryLanguage ?? ""}
            onChange={(event) => onChange({ ...value, secondaryLanguage: readReplyLanguage(event.target.value) })}
            disabled={!value.primaryLanguage}
            className={cn(selectClass, !value.primaryLanguage && "opacity-60")}
          >
            {!value.primaryLanguage && <option value="">Not set</option>}
            {languages
              .filter((language) => language !== value.primaryLanguage)
              .map((language) => (
                <option key={language} value={language}>{REPLY_LANGUAGE_LABELS[language]}</option>
              ))}
          </select>
        </label>
      </div>
      <label className="mt-3 block">
        <span className="mb-1.5 block text-xs font-bold text-ink">Switch share</span>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={99}
            step={5}
            value={percent}
            disabled={!value.primaryLanguage}
            onChange={(event) => {
              const next = Math.round(Number(event.target.value));
              onChange({ ...value, languageSwitchPercent: Number.isFinite(next) ? Math.min(99, Math.max(1, next)) : DEFAULT_SWITCH_PERCENT });
            }}
            className={cn("h-10 w-24 rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand", !value.primaryLanguage && "opacity-60")}
          />
          <span className="text-xs text-ink-3">%</span>
        </div>
        <span className="mt-1.5 block text-[10px] leading-4 text-ink-3">
          {value.primaryLanguage && value.secondaryLanguage
            ? `Replies switch to ${REPLY_LANGUAGE_LABELS[value.secondaryLanguage]} only when more than ${percent}% of the caller's turn is in it; otherwise they stay in ${REPLY_LANGUAGE_LABELS[value.primaryLanguage]}.`
            : "Pick a primary language to make the agent stick to it."}
        </span>
      </label>
    </div>
  );
}
